import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Footer from './Footer';
import Alert from '../common/Alert';
import { testimonialService } from '../../services/testimonialService';
import { contactService } from '../../services/contactService';
import banner from '../../static/images/banner.png';
import lineBG from '../../static/images/line-bg.png';
import { ArrowRight, PackageSearch, Sparkles } from "lucide-react";
import { useModal } from "../../context/ModalContext";
import { useTranslation } from 'react-i18next';
import { APK_RELEASE } from '../../config/apkRelease';
import ApkLaunchModal from './ApkLaunchModal';




const NAVBAR_HEIGHT = 80; // matches your fixed navbar height
const products = [
  {
    title: "Logix-DQ",
    subtitle: "Logistics & Transportation Platform",
    content: (
      <>
        <p>
          Logix-DQ is a comprehensive digital solution designed to simplify and automate the end-to-end process of shipment booking and transportation management. It enables logistics companies, transporters, and cargo operators to create bookings quickly by capturing pickup and delivery details, shipment type, weight, freight charges, and vehicle requirements—all from a single platform.
        </p>
        <p>
          The software streamlines vehicle allocation, driver assignment, and route planning, ensuring optimal utilization of fleet resources and timely dispatches. With real-time shipment tracking and status updates, businesses gain complete visibility over their operations while customers stay informed at every stage of delivery.
        </p>
        <p>
          Integrated billing and invoicing features allow automatic freight calculation and generation of GST-compliant, computer-generated invoices, reducing manual errors and saving time. Built-in MIS and performance reports provide valuable insights into bookings, revenue, vehicle performance, and outstanding payments, supporting better decision-making.
        </p>
        <p>
          Being cloud-based and secure, Logix-DQ Platform can be accessed anytime, anywhere, making it ideal for growing and multi-location operations. By reducing paperwork, improving accuracy, and accelerating workflows, it helps businesses operate more efficiently, deliver better customer service, and scale with confidence in a competitive logistics environment.
        </p>
      </>
    ),
  },
  {
    title: "IMS-DQ",
    subtitle: "Smart Inventory Management System",
    content: (
      <>
        <p>
          IMS-DQ is a smart, centralized solution designed to help businesses efficiently track, control, and optimize their inventory operations. It enables real-time monitoring of stock levels across warehouses, stores, or locations, ensuring accurate visibility of available inventory at all times.
        </p>
        <p>
          Platform simplifies key processes such as stock inward and outward entries, purchase and sales management, batch and item-wise tracking, and reorder level alerts. By automating these activities, it reduces manual errors, prevents stock shortages or overstocking, and improves overall operational efficiency.
        </p>
        <p>
          Integrated reporting and MIS dashboards provide valuable insights into stock movement, inventory valuation, slow-moving items, and demand trends, empowering businesses to make informed decisions. The system also supports supplier and customer management, helping maintain smooth coordination throughout the supply chain.
        </p>
        <p>
          With cloud-based access and secure data management, IMS-DQ allows authorized users to manage inventory anytime and from anywhere. It is suitable for businesses of all sizes, including retail, wholesale, manufacturing, and distribution. By streamlining inventory control and improving accuracy, the software helps reduce costs, enhance productivity, and support scalable business growth.
        </p>
      </>
    ),
  },
  {
    title: "Web & Digital Marketing",
    subtitle: "Growth-Driven Digital Presence",
    content: (
      <>
        <p>
          Web & Digital Marketing is a comprehensive solution designed to help businesses establish a strong online presence and achieve measurable growth in the digital space. It combines professional website development with result-driven digital marketing strategies to ensure your brand not only looks credible but also reaches the right audience at the right time.
        </p>
        <p>
          Our web development services focus on creating responsive, fast, and user-friendly websites that work seamlessly across all devices. Each website is built with modern design standards, strong security, and scalability in mind, ensuring long-term performance and easy future enhancements.
        </p>
        <p>
          The digital marketing component drives visibility and engagement through search engine optimization (SEO), paid advertising, social media marketing, content creation, and performance tracking. Data-driven insights and analytics are used to monitor campaigns, optimize conversions, and maximize return on investment.
        </p>
        <p>
          By integrating website development with digital marketing, businesses benefit from consistent branding, better user experience, and improved lead generation. Whether you are a start-up or an established enterprise, Web & Digital Marketing helps you attract customers, build trust, and grow sustainably in an increasingly competitive digital environment.
        </p>
      </>
    ),
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [openChallenge, setOpenChallenge] = useState(false);
  const [openApkModal, setOpenApkModal] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const [activeProduct, setActiveProduct] = useState(0);
  const { openJoinModal } = useModal();

  const [testimonials, setTestimonials] = useState([]);
  const [shipmentForm, setShipmentForm] = useState({
    name: '',
    phone: '',
    from_location: '',
    to_location: ''
  });
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    phone: "",
    product: "",
    password: "",
    confirmPassword: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    testimonialService.getFeatured(6).then(res => {
      setTestimonials(res.data?.data || []);
    });
  }, []);

  const handleScheduleShipment = async () => {
    if (!shipmentForm.name || !shipmentForm.phone) {
      Alert.error('Please fill all required fields');
      return;
    }

    const confirmed = await Alert.confirm(
      'Are you sure you want to submit this shipment request?',
      'Confirm Shipment Request'
    );

    if (!confirmed) return;

    setSubmitting(true);
    try {
      await contactService.scheduleShipment(shipmentForm);
      Alert.success('Shipment request submitted successfully.');
      setShipmentForm({
        name: '',
        phone: '',
        from_location: '',
        to_location: ''
      });
    } catch {
      Alert.error('Failed to submit request.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-50">

      {/* ================= HERO (TRUE 100VH) ================= */}
      <section
        className="relative min-h-screen md:h-screen bg-tertiary overflow-x-hidden"
        style={{ paddingTop: NAVBAR_HEIGHT }}
      >
        {/* LEFT BACKGROUND GRADIENT */}
        <div
          className="
            absolute inset-y-0 left-0
            w-[60%]
            bg-gradient-to-r
            from-white via-white/95 to-transparent
            z-0
          "
        />

        {/* CONTENT WRAPPER */}
        <div className="container mx-auto px-4 relative z-20 h-full">
          <div className="grid grid-cols-1 md:grid-cols-2 h-full items-center">

            {/* LEFT CONTENT */}
            <div className="space-y-3 max-w-2xl relative z-30 pb-10 md:pb-0">

              <div className="flex flex-wrap items-center gap-3">
                <p className="inline-block text-sm uppercase tracking-widest text-purple-600 font-semibold bg-gradient-to-r
 from-purple-100 via-purple-50 to-indigo-100 px-4 py-1.5 rounded-full font-semibold
 text-purple-700">
                  About DigiQin
                </p>

                {APK_RELEASE.enabled && (
                  <button
                    type="button"
                    onClick={() => setOpenApkModal(true)}
                    className="
                      group relative inline-flex items-center gap-2
                      px-3.5 py-1.5 rounded-full
                      text-xs sm:text-sm font-semibold
                      text-white
                      bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600
                      shadow-[0_8px_24px_rgba(124,58,237,0.35)]
                      transition-all duration-300
                      hover:shadow-[0_12px_32px_rgba(124,58,237,0.45)]
                      hover:-translate-y-0.5
                      animate-float
                    "
                  >
                    <span className="absolute -inset-0.5 rounded-full bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 opacity-40 blur-sm group-hover:opacity-60 transition" />
                    <Sparkles className="relative w-3.5 h-3.5" />
                    <span className="relative">{APK_RELEASE.highlightLabel}</span>
                    <span className="relative inline-flex items-center justify-center px-1.5 py-0.5 rounded-md bg-white/20 text-[10px] uppercase tracking-wide">
                      APK
                    </span>
                  </button>
                )}
              </div>

              <h1 className="text-4xl md:text-4xl font-semibold text-gray-900 leading-tight">
                Be 10X more efficient with
                <span
                  className="
                    block mt-2 pb-2
                    text-4xl md:text-4xl font-semibold
                    bg-gradient-to-r
                    from-pink-800 via-purple-600 to-indigo-600
                    bg-clip-text text-transparent
                  "
                >
                  Intelligent Logistics Platform
                </span>
              </h1>

              <p className="text-md text-gray-400 max-w-lg">
                Shipment to Settlement. Fully Digital.
              </p>

              <p className="pb-2 text-md text-gray-600 leading-relaxed max-w-lg">
                Manage shipment booking, track expenses, monitor deliveries, and generate profit & loss reports — all from one unified logistics platform. DigiQin helps businesses move faster in a digital-first world.
              </p>

              <div
                className="
                  mt-6
                  max-w-lg
                  flex flex-col sm:flex-row gap-4 sm:gap-6 py-4 px-4
                  border-t border-b border-purple-100
                  bg-gradient-to-r from-white via-purple-50 to-white
                "
              >
                <div className="flex items-center gap-3 text-gray-500">
                  <span className="flex items-center justify-center
                                  w-5 h-5 rounded-full
                                  bg-purple-100 text-purple-700
                                  text-xs font-bold">
                    ✓
                  </span>
                  <span className="text-sm font-regular">
                    From Booking to Delivery, Digitally.
                  </span>
                </div>

                <div className="flex items-center gap-3 text-gray-500">
                  <span className="flex items-center justify-center
                                  w-5 h-5 rounded-full
                                  bg-purple-100 text-purple-700
                                  text-xs font-bold">
                    ✓
                  </span>
                  <span className="text-sm font-regular">
                    Inventory, Simplified.
                  </span>
                </div>
              </div>


              <div className="pt-4 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 w-full max-w-lg">
                {/* PRIMARY CTA */}
                <button
                  onClick={openJoinModal}
                  className="
                    group relative inline-flex items-center justify-center gap-3
                    w-full sm:w-auto
                    px-5 py-2 rounded-full
                    bg-purple-900
                    text-white font-semibold text-sm
                    transition-all duration-300
                    hover:shadow-[0_12px_35px_rgba(99,102,241,0.4)]
                    animate-float
                  "
                >
                  <span>Join Platform</span>

                  <span
                    className="
                      flex items-center justify-center
                      w-7 h-7 rounded-full
                      bg-white/10 backdrop-blur-md
                      transition-all duration-300
                      group-hover:bg-white/20
                      group-hover:translate-x-1
                    "
                  >
                    <ArrowRight className="w-4 h-4 text-white" />
                  </span>
                </button>

                {/* SECONDARY ACTION — SOLUTION */}
                <button
                  onClick={() => setOpenChallenge(true)}
                  className="
                    inline-flex items-center justify-center
                    w-full sm:w-auto
                    px-4 py-3 rounded-full
                    text-sm font-semibold
                    text-purple-700
                    border border-purple-300
                    bg-white
                    transition-all duration-300
                    hover:shadow-[0_12px_35px_rgba(99,102,241,0.4)]
                    animate-float
                  "
                >
                  <span className="inline-flex items-center gap-2">
                    Challenges
                    <ArrowRight className="w-4 h-4 text-purple-700" />
                    Solutions
                  </span>
                </button>

                {/* TRACK SHIPMENT */}
                <button
                  type="button"
                  onClick={() => navigate('/track-shipment')}
                  className="
                    inline-flex items-center justify-center gap-2
                    w-full sm:w-auto
                    px-4 py-3 rounded-full
                    text-sm font-semibold
                    text-white
                    bg-blue-600
                    border border-blue-600
                    transition-all duration-300
                    hover:bg-blue-700 hover:shadow-[0_12px_35px_rgba(37,99,235,0.35)]
                  "
                >
                  <PackageSearch className="w-4 h-4" />
                  Track Shipment
                </button>

              </div>

            </div>
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div
          className="
            hidden md:block
            absolute right-0 top-1/2 -translate-y-1/2
            w-[60vw] z-10 pointer-events-none
          "
        >
          <img
            src={banner}
            alt="Logistics Dashboard"
            className="w-full object-contain opacity-95"
          />

          <div
            className="
              absolute inset-y-0 left-0
              w-72
              bg-gradient-to-r
              from-white via-white/80 to-transparent
            "
          />
        </div>
      </section>

      <ApkLaunchModal
        open={openApkModal}
        onClose={() => setOpenApkModal(false)}
      />

      {openChallenge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center
                        bg-black/40 backdrop-blur-sm">

          {/* TOOLBOX */}
          <div className="bg-white max-w-4xl w-full mx-4 rounded-xl shadow-2xl
                          animate-[fadeIn_0.25s_ease]">

            {/* HEADER */}
            <div className="flex items-center justify-between px-6 py-4
                            bg-gradient-to-r from-purple-600 to-indigo-600
                            rounded-t-xl">
              <h3 className="text-lg font-semibold text-white">
                Challenge → DQ Solutions → Key Benefits
              </h3>

              <button
                onClick={() => setOpenChallenge(false)}
                className="text-white text-xl hover:opacity-80"
              >
                ✕
              </button>
            </div>

            {/* CONTENT */}
            <div className="p-6 overflow-x-auto">

              <table className="w-full border border-gray-200 text-lg rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left font-semibold text-green-700 border">
                      CHALLENGES YOU FACE
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-green-700 border">
                      DQ – SOLUTIONS WE PROVIDE
                    </th>
                    <th className="px-4 py-3 text-left font-semibold text-green-700 border">
                      KEY BENEFITS
                    </th>
                  </tr>
                </thead>

                <tbody className="text-gray-700 text-sm">
                  <tr className="hover:bg-purple-50/40 transition">
                    <td className="px-4 py-3 border">
                      Manual booking & trip registers
                    </td>
                    <td className="px-4 py-3 border">
                      Digital vehicle management on a centralized platform
                    </td>
                    <td className="px-4 py-3 border">
                      Faster booking & dispatch
                    </td>
                  </tr>

                  <tr className="bg-gray-50 hover:bg-purple-50/40 transition">
                    <td className="px-4 py-3 border">
                      No real-time vehicle visibility
                    </td>
                    <td className="px-4 py-3 border">
                      Vehicle-wise & driver-wise real-time tracking
                    </td>
                    <td className="px-4 py-3 border">
                      Better vehicle utilization
                    </td>
                  </tr>

                  <tr className="hover:bg-purple-50/40 transition">
                    <td className="px-4 py-3 border">
                      Delayed POD & payment follow-ups
                    </td>
                    <td className="px-4 py-3 border">
                      Digital POD upload with live payment status tracking
                    </td>
                    <td className="px-4 py-3 border">
                      Reduced disputes & losses
                    </td>
                  </tr>

                  <tr className="bg-gray-50 hover:bg-purple-50/40 transition">
                    <td className="px-4 py-3 border">
                      Manual freight calculations
                    </td>
                    <td className="px-4 py-3 border">
                      Automated freight calculation & vehicle performance tracking
                    </td>
                    <td className="px-4 py-3 border">
                      Clear trip profitability reports
                    </td>
                  </tr>
                </tbody>
              </table>

            </div>

            {/* FOOTER */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-xl">
              <button
                onClick={() => setOpenChallenge(false)}
                className="px-4 py-2 rounded-md
                          bg-purple-900 text-white text-sm font-semibold
                          hover:shadow-lg transition"
              >
                Close
              </button>
            </div>

          </div>
        </div>
      )}


      {/* ================= PRODUCT SECTIONS ================= */}
      <section className="relative py-16 bg-white overflow-hidden">

        {/* ===== CREATIVE GRID PATTERN BACKGROUND ===== */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">

          {/* GRID LINES */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(124,58,237,0.08) 2px, transparent 1px),
                linear-gradient(to bottom, rgba(124,58,237,0.08) 2px, transparent 1px)
              `,
              backgroundSize: "32px 32px",
            }}
          />

          {/* CENTER SOFT GLOW */}
          <div className="absolute inset-0 bg-gradient-to-r from-white via-purple-100/40 to-white" />
        </div>

        {/* ===== CONTENT ===== */}
        <div className="relative container mx-auto px-4">

          {/* HEADER */}
          <div className="max-w-4xl mb-8">
            <p
              className="
                inline-block text-sm uppercase tracking-widest
                bg-gradient-to-r from-purple-100 via-purple-50 to-indigo-100
                px-4 py-1.5 rounded-full font-semibold text-purple-700 mb-3
              "
            >
              Our Solutions
            </p>

            <h2 className="text-3xl font-semibold text-gray-800">
              Discover Our Range of Software Solutions.
            </h2>

            <p className="mt-4 text-gray-600 text-md">
              Purpose-built software solutions designed for efficiency, scale, and control.
            </p>
          </div>

          {/* SECTIONS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">

            {/* ================= CARD BASE STYLE ================= */}
            {[
              {
                title: "DQ-LOGIX",
                items: [
                  "Digital Shipment Booking",
                  "Live Trip & Vehicle Tracking",
                  "Trip-Wise Expense Capture",
                  "POD & Settlement Tracking",
                  "Shipment-Level P&L",
                  "Secure & Scalable Platform",
                ],
                link: "/dq-logix",
                cta: "Explore platform",
              },
              {
                title: "DQ-IMS",
                items: [
                  "Real-Time Stock Tracking",
                  "SKU Control",
                  "Multi-Warehouse Management",
                  "Low-Stock Alerts",
                  "Purchase & Sales Integration",
                  "Inventory Reports & Valuation",
                ],
                link: "/ims-dq",
                cta: "Explore platform",
              },
              {
                title: "DQ – Website & DM",
                items: [
                  "Website Design & Development",
                  "SEO-Ready Architecture",
                  "Mobile-First & Fast Loading",
                  "Google Ads & Social Campaigns",
                  "Content & Brand Strategy",
                  "Analytics & Performance Tracking",
                ],
                link: "/website-dm",
                cta: "Explore services",
              },
            ].map((card, idx) => (
              <div
                key={idx}
                className="
                  group relative px-6 py-8
                  rounded-lg
                  transition-all duration-300

                  bg-transparent
                  hover:bg-white/20

                  backdrop-blur-[2px]

                  border border-transparent
                  hover:border-purple-200

                  hover:shadow-md
                  hover:-translate-y-1
                "
              >
                {/* LEFT ACCENT */}
                <span
                  className="
                    absolute left-0 top-8 h-12 w-1 bg-purple-600
                    rounded-full opacity-0 group-hover:opacity-100 transition
                  "
                />

                {/* TITLE */}
                <h3 className="mb-6">
                  <span
                    className="
                      relative block text-2xl font-semibold
                      bg-gradient-to-r from-pink-800 via-purple-600 to-indigo-600
                      bg-clip-text text-transparent
                      pb-4
                      after:content-['']
                      after:absolute after:left-0 after:bottom-0
                      after:h-[1px] after:w-full
                      after:bg-gradient-to-r after:from-purple-300 after:to-white
                    "
                  >
                    {card.title}
                  </span>
                </h3>

                {/* LIST */}
                <ul className="space-y-2 text-gray-600">
                  {card.items.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <span
                        className="
                          mt-1 flex items-center justify-center
                          w-5 h-5 rounded-full
                          bg-gray-200 text-purple-700
                          text-xs font-bold
                        "
                      >
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to={card.link}
                  className="
                    group inline-flex items-center gap-1 mt-7
                    px-4 py-2 rounded-md
                    bg-purple-900 text-white
                    text-sm font-semibold
                    transition-all duration-300
                    hover:shadow-[0_10px_30px_rgba(99,102,241,0.4)]
                  "
                >
                  {card.cta}
                  <span
                    className="
                      flex items-center justify-center w-6 h-6
                      transition-transform duration-300 group-hover:translate-x-1
                    "
                  >
                    →
                  </span>
                </Link>
              </div>
            ))}

          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default LandingPage;
